import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ReloadIcon } from "@radix-ui/react-icons";
import CollectionService from "@/services/CollectionService";
import useStore from "@/store";
import { DEFAULT_LEARNING_LANGUAGE } from "@/constants/langcraftLanguages";
import LanguagePicker from "@/components/LanguagePicker";

const defaultLanguage = DEFAULT_LEARNING_LANGUAGE;


const MIN_COUNT = 1;
const MAX_COUNT = 100;

const AIGenerationDialog = ({ open, onClose, onCollectionCreated }) => {
  const navigate = useNavigate();
  const store = useStore();
  const { setCurrentCollectionId } = store.user;
  const { setCurrentCollectionById } = store.collections;
  const [prompt, setPrompt] = useState("");
  const [language, setLanguage] = useState(defaultLanguage);
  const [count, setCount] = useState(10);
  const [isGenerating, setIsGenerating] = useState(false);

  const resetForm = () => {
    setPrompt("");
    setLanguage(defaultLanguage);
    setCount(10);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast("Missing prompt", {
        description: "Please provide a prompt description before generating.",
      });
      return;
    }

    if (count < MIN_COUNT || count > MAX_COUNT) {
      toast("Invalid number of cards", {
        description: `Please choose a number between ${MIN_COUNT} and ${MAX_COUNT}.`,
      });
      return;
    }

    try {
      setIsGenerating(true);
      const payload = {
        prompt: prompt.trim(),
        language,
        count,
      };
      const generatedCollection =
        await CollectionService.generateCollectionWithAI(
          payload.prompt,
          payload.language,
          payload.count
        );

      toast("Collection generated", {
        description: "Your AI-generated collection is ready to practice!",
      });

      await setCurrentCollectionById(generatedCollection.id);
      setCurrentCollectionId(generatedCollection.id);
      if (onCollectionCreated) {
        await onCollectionCreated(generatedCollection);
      }

      navigate("/");
      onClose();
      resetForm();
    } catch (error) {
      console.error("Failed to generate collection via AI:", error);
      toast("Error", {
        description:
          error?.response?.data?.error ||
          "Unable to generate collection right now. Please try again later.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl rounded-[28px] border border-line bg-surface p-6 text-fg shadow-panel backdrop-blur-2xl">
        <div className="space-y-6">
          <div className="space-y-2">
            <DialogTitle className="text-2xl font-semibold tracking-tight text-fg">
              Generate Using AI
            </DialogTitle>
            <p className="text-sm text-fg-muted">
              Provide a prompt and let AI draft a collection description for you.
            </p>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="ai-prompt"
                className="text-sm font-medium text-fg-muted"
              >
                Prompt Description
              </Label>
              <Textarea
                id="ai-prompt"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder="Describe the topic or scenario you want cards for..."
                className="min-h-[120px] rounded-2xl border border-line bg-surface/80 px-4 py-3 text-sm text-fg placeholder:text-fg-subtle transition focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label
                  htmlFor="ai-language"
                  className="text-sm font-medium text-fg-muted"
                >
                  Language
                </Label>
                <LanguagePicker
                  id="ai-language"
                  value={language}
                  onChange={setLanguage}
                  triggerClassName="h-12 rounded-2xl bg-surface/80 px-4"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="ai-count"
                  className="text-sm font-medium text-fg-muted"
                >
                  Number of Cards
                </Label>
                <Input
                  id="ai-count"
                  type="number"
                  min={MIN_COUNT}
                  max={MAX_COUNT}
                  value={count}
                  onChange={(event) => {
                    const nextValue = Number(event.target.value);
                    setCount(Number.isNaN(nextValue) ? MIN_COUNT : nextValue);
                  }}
                  className="h-12 rounded-2xl border border-line bg-surface/80 px-4 text-sm text-fg transition focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center pt-2">
            <Button
              className="flex w-full max-w-[220px] items-center justify-center rounded-full border border-primary/50 bg-gradient-to-r from-primary to-primary px-6 py-3 text-sm font-semibold text-primary-fg shadow-panel transition hover:shadow-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                "Generate"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIGenerationDialog;
